import axios from 'axios'
import { mergeAgentsQueryIntoUrl } from '@/utils/agentsApi'

// PAICE chat has pre-conditional check for settings concurrency
export const NO_SETTINGS_STORED = '*'
export const settingsVersionToken = (settings) => settings?.updatedAt || NO_SETTINGS_STORED

/**
 * Build the conditional header asserting which settings version a chat send is
 * based on.
 *
 * Exclusive to `POST .../aquabot`: the stream is the one place a mid-compose
 * settings change still matters, because answering under a prompt/model the user
 * never saw silently contradicts what the UI showed.
 *
 * A concrete `updatedAt` goes in `If-Match`, so the send is rejected (412) if
 * someone else has written since. When the caller read no stored settings there
 * is no validator to echo, so `If-None-Match: *` asserts "still unconfigured"
 * and catches a concurrent first-time create. Sending neither header (only when
 * the read itself failed, so there is genuinely nothing to assert) skips the
 * check server-side.
 *
 * @param {?string} expectedUpdatedAt - updatedAt from the last read, the '*'
 *        sentinel for "nothing stored", or null/undefined to skip the check
 */
export function settingsPreconditionHeaders(expectedUpdatedAt) {
    if (!expectedUpdatedAt) return {}
    return expectedUpdatedAt === NO_SETTINGS_STORED
        ? { 'If-None-Match': NO_SETTINGS_STORED }
        : { 'If-Match': expectedUpdatedAt }
}

const store = {
    namespaced: true,
    state: {
        // Agent settings
        paiceSettings: null,
        availableModels: [],
        modelCatalogSource: null,
        modelCatalogRegion: null,

        // The cid's context layer, as authored. `null` means "not fetched yet";
        // an empty string means "fetched, and this cid has never authored one"
        // (the service 404s in that case, which is not an error).
        contextLayer: null,

        // Roles
        standardRoles: [1, 1.5, 2, 3], // Can view, create, and modify resources
        privilegedRoles: [1, 1.5, 3], // Can perform privileged operations also (e.g., chat deletes, settings/context layer updates)

        // Chat history result limits
        chatLimit: 50,
        adminChatLimit: 50,

        // Cached chat lists, kept separate so switching scope does not refetch.
        // `null` means "never fetched" — the only state that triggers a load;
        // an empty array is a real result (this cid/user has no chats).
        chats: null,
        adminChats: null,
    },
    mutations: {
        setPaiceSettings(state, settings) {
            state.paiceSettings = settings
        },
        setModelCatalog(state, payload) {
            state.availableModels = payload?.models || []
            state.modelCatalogSource = payload?.source || null
            state.modelCatalogRegion = payload?.region || null
        },
        setContextLayer(state, yamlText) {
            state.contextLayer = yamlText
        },
        setChatLimit(state, limit) {
            state.chatLimit = limit
        },
        setAdminChatLimit(state, limit) {
            state.adminChatLimit = limit
        },
        setChats(state, chats) {
            state.chats = chats
        },
        setAdminChats(state, chats) {
            state.adminChats = chats
        },
    },
    actions: {
        onClientChange({ commit }) {
            commit('setPaiceSettings', null)
            commit('setModelCatalog', null)
            commit('setContextLayer', null)
            commit('setChats', null)
            commit('setAdminChats', null)
        },
        setChatLimit({ commit }, limit) {
            commit('setChatLimit', limit)
        },
        setAdminChatLimit({ commit }, limit) {
            commit('setAdminChatLimit', limit)
        },

        /**
         * Fetch the chat list for the current cid, newest first.
         *
         * Cached per scope: a already-populated list is returned untouched unless
         * `forceGet` is set, so toggling between "Mine" and "All users" costs one
         * request each per session rather than one per toggle. Callers that
         * mutate the list (new chat, delete, retitle) pass `forceGet` or commit
         * the change directly.
         *
         * @param {Object} [payload]
         * @param {boolean} [payload.adminHistory=false] - every user's chats
         * @param {boolean} [payload.forceGet=false] - refetch even if cached
         * @param {number} [payload.limit] - max chats to return
         */
        async getChats({ commit, state, rootState }, { adminHistory = false, forceGet = false, limit } = {}) {
            const cached = adminHistory ? state.adminChats : state.chats
            if (!forceGet && cached != null) return cached

            const endpoint = mergeAgentsQueryIntoUrl(`${rootState.agentsUrl}/v1/cids/${rootState.currentClient.cid}/aquabot/chats`, rootState);
            return axios.get(endpoint, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
                params: {
                    ...(limit ? { limit } : {}),
                    ...(adminHistory ? { admin_history: true } : {}),
                },
            }).then((response) => {
                const chats = (response?.data?.items || []).sort((a, b) => {
                    return new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0)
                })
                commit(adminHistory ? 'setAdminChats' : 'setChats', chats)
                return chats
            }).catch((e) => {
                console.error("Error fetching paice chats: ", e)
                throw e;
            })
        },

        /** Replace the cached list for one scope (after a create/delete/retitle). */
        setChats({ commit }, { chats, adminHistory = false } = {}) {
            commit(adminHistory ? 'setAdminChats' : 'setChats', chats)
        },

        /**
         * Bedrock models this account can invoke, for the model picker. This is a
         * convenience list and not an allowlist — writes accept any id — so a
         * failure here must not block the settings page.
         */
        async getAvailableModels({ commit, rootState }) {
            const endpoint = mergeAgentsQueryIntoUrl(`${rootState.agentsUrl}/v1/aquabot/models`, rootState);
            return axios.get(endpoint, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            }).then((response) => {
                commit('setModelCatalog', response?.data || {})
                return response
            }).catch((e) => {
                console.error("Error fetching paice model catalog: ", e)
                throw e;
            })
        },

        /**
         * Get all stored settings.
         *
         * @param {Object} [payload]
         * @param {boolean} [payload.commit=true] - pass false to read the stored
         *        settings without making them the ones in effect. The chat's
         *        conflict notice needs to show what changed before the user has
         *        agreed to run under it, so it reads with commit: false and
         *        commits via `applyPaiceSettings` only on accept.
         */
        async getPaiceSettings({ commit, rootState }, { commit: shouldCommit = true } = {}) {
            const endpoint = mergeAgentsQueryIntoUrl(`${rootState.agentsUrl}/v1/cids/${rootState.currentClient.cid}/aquabot/settings`, rootState);
            return axios.get(endpoint, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            }).then((response) => {
                let settings = response?.data || {};
                if (shouldCommit) {
                    commit('setPaiceSettings', settings)
                }
                return response
            }).catch((e) => {
                console.error("Error fetching paice settings: ", e)
                throw e;
            })
        },

        /** Make an already-fetched settings object the one in effect. */
        applyPaiceSettings({ commit }, settings) {
            commit('setPaiceSettings', settings || null)
        },

        /**
         * Create or replace settings for the current cid.
         *
         * @param {Object} payload
         * @param {?string} payload.systemPrompt - null/empty clears the override
         * @param {?string} payload.modelId - null/empty reverts to the service default
         *
         * The write is unconditional — the service takes the last writer, so a
         * concurrent edit by another admin is overwritten rather than rejected.
         * Staleness is only enforced on the chat stream, which asserts the version
         * it was composed under (see `settingsPreconditionHeaders`).
         */
        async savePaiceSettings({ commit, rootState }, { systemPrompt, modelId }) {
            const endpoint = mergeAgentsQueryIntoUrl(`${rootState.agentsUrl}/v1/cids/${rootState.currentClient.cid}/aquabot/settings`, rootState);
            return axios.put(endpoint, {
                systemPrompt: systemPrompt || null,
                modelId: modelId || null,
            }, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            }).then((response) => {
                commit('setPaiceSettings', response?.data || {})
                return response
            }).catch((e) => {
                console.error("Error saving paice settings: ", e)
                throw e;
            })
        },

        /**
         * Delete all stored settings, reverting the cid to service defaults.
         *
         * Unconditional, like the write: the service 404s only when there was
         * nothing stored to delete.
         */
        async deletePaiceSettings({ commit, rootState }) {
            const endpoint = mergeAgentsQueryIntoUrl(`${rootState.agentsUrl}/v1/cids/${rootState.currentClient.cid}/aquabot/settings`, rootState);
            return axios.delete(endpoint, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                },
            }).then((response) => {
                commit('setPaiceSettings', response?.data || {})
                return response
            }).catch((e) => {
                console.error("Error deleting paice settings: ", e)
                throw e;
            })
        },

        /**
         * Fetch the cid's context layer — the semantic description of its own
         * datasets, metrics and joins that grounds PAICE's answers.
         *
         * The service returns the raw YAML byte-for-byte as authored, not a JSON
         * envelope, so the response is read as text. A 404 means this cid has
         * never authored one, which is a normal empty state rather than a
         * failure: it resolves to "" so callers can render the blank editor.
         */
        async getContextLayer({ commit, rootState }) {
            const endpoint = mergeAgentsQueryIntoUrl(`${rootState.agentsUrl}/v1/cids/${rootState.currentClient.cid}/context`, rootState);
            return axios.get(endpoint, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                    Accept: 'application/yaml',
                },
                // Without this axios sniffs the body and would hand back a parsed
                // object for anything JSON-ish, losing the authored formatting.
                responseType: 'text',
                transformResponse: [(data) => data],
            }).then((response) => {
                // The global interceptor resolves with `undefined` when a request
                // fails without a response (network/CORS). Treating that as an
                // empty layer would show the "not configured" state for a cid
                // that has one, and let the next save silently replace it.
                if (!response) {
                    throw new Error('No response received while fetching the context layer')
                }
                const text = typeof response.data === 'string' ? response.data : ''
                commit('setContextLayer', text)
                return text
            }).catch((e) => {
                if (e?.response?.status === 404) {
                    // Never authored one — an empty state, not an error.
                    commit('setContextLayer', '')
                    return ''
                }
                console.error("Error fetching paice context layer: ", e)
                throw e;
            })
        },

        /**
         * Validate and store the context layer for the current cid.
         *
         * The body is the YAML document itself rather than a JSON envelope, and
         * a client has exactly one layer, so this replaces whatever was stored.
         * The service validates strictly (unknown keys are rejected) and returns
         * 400 with every violation flattened to a `domains.0.datasets.2.name`
         * style path; nothing is stored when that happens.
         *
         * @param {string} yamlText - the document exactly as authored
         */
        async saveContextLayer({ commit, rootState }, yamlText) {
            const endpoint = mergeAgentsQueryIntoUrl(`${rootState.agentsUrl}/v1/cids/${rootState.currentClient.cid}/context`, rootState);
            return axios.put(endpoint, yamlText, {
                headers: {
                    Authorization: `Bearer ${rootState.accessToken}`,
                    'Content-Type': 'application/yaml',
                },
            }).then((response) => {
                // As in getContextLayer: a resolved-but-empty response means the
                // request never completed, so don't report it as saved.
                if (!response) {
                    throw new Error('No response received while saving the context layer')
                }
                // The response carries only a status message, so the stored copy
                // is what we just sent.
                commit('setContextLayer', yamlText)
                return response
            }).catch((e) => {
                console.error("Error saving paice context layer: ", e)
                throw e;
            })
        },
    }
}

export default store
